import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import { useAlerts } from "@/shared/api/hooks/alerts";
import { useSites } from "@/shared/api/hooks/sites";
import { qk } from "@/shared/api/keys";
import { useAlertChannel } from "@/shared/api/ws";
import { selectUser, useAuth } from "@/shared/auth/store";
import { useAlertTTS } from "@/shared/hooks/useAlertTTS";

/** 이보다 오래된 경보는 「지금 발생한 것」으로 보지 않는다(백로그 낭독 방지). */
const FRESH_WINDOW_MS = 10 * 60 * 1000;

/**
 * Headless app-wide announcer. Mounted once in <AppShell> so new alerts
 * are read aloud (when TTS is enabled) on every page, not just the alerts
 * view.
 *
 * Source of truth is the same `useAlerts` query the rest of the app uses
 * (polled every 30 s); we additionally subscribe to the org alert channel
 * to invalidate that query the instant a new alert fires — identical to
 * AlertsPage — so the announcement is near-real-time without a second
 * data path. Dedupe lives in `useAlertTTS` (per alert id), so re-renders
 * and overlapping mounts never double-speak.
 */
export function AlertTtsAnnouncer() {
  const { announce } = useAlertTTS();
  const qc = useQueryClient();
  const user = useAuth(selectUser);
  const orgId = user?.organization?.id ?? null;

  const { data: alertsData } = useAlerts();
  const { data: sitesData } = useSites();

  const siteName = useMemo(() => {
    const m = new Map<number, string>();
    (sitesData?.results ?? []).forEach((s) => {
      if (s.id != null) m.set(s.id, s.name);
    });
    return (id: number | string) => m.get(Number(id)) ?? "";
  }, [sitesData]);

  /**
   * 이미 쌓여 있던 경보 id — <b>기준선</b>. 첫 로드 때 목록에 있던 것들은 「지금 발생한 사건」이
   * 아니므로 읽지 않고 여기에만 담는다.
   *
   * <p>⚠️ 2026-08-31 사고: 음성경보 기본값을 켜자 화면을 열 때마다 <b>과거 경보 12건이 줄줄이
   * 낭독</b>됐다. 며칠 전(7월) 것까지 포함이었다. 원인은 이 컴포넌트가 목록이 바뀔 때마다
   * <b>목록 전체</b>를 넘기고, 중복방지 Set 은 훅 안에 있어 <b>새로고침하면 비기</b> 때문이다.
   * 「경보가 발령됐다」와 「경보 목록을 처음 받았다」는 전혀 다른 사건인데 구분이 없었다.</p>
   */
  const seen = useRef<Set<number> | null>(null);

  // Push new alerts into the announcer whenever the list changes.
  const alerts = alertsData?.results ?? [];
  useEffect(() => {
    if (!alerts.length) return;
    const items = alerts
      .filter((a) => a.id != null)
      .map((a) => ({
        id: a.id!,
        level: a.level ?? 0,
        site: a.site,
        gas: a.gas,
        raisedAt: a.raised_at,
      }));

    if (seen.current === null) {
      seen.current = new Set(items.map((i) => i.id));   // 기준선만 잡고 침묵
      return;
    }

    const fresh = items.filter((i) => !seen.current!.has(i.id));
    if (!fresh.length) return;
    fresh.forEach((i) => seen.current!.add(i.id));

    // 발생 시각이 오래된 건은 읽지 않는다 — 네트워크 복구·페이지 전환으로 뒤늦게 도착한
    // 백로그를 「지금 터진 경보」처럼 방송하면 현장이 잘못 움직인다.
    const now = Date.now();
    const recent = fresh.filter(
      (i) => !i.raisedAt || now - Date.parse(i.raisedAt) < FRESH_WINDOW_MS,
    );
    if (recent.length) announce(recent, siteName);
  }, [alerts, announce, siteName]);

  // Refresh the alert list the moment a new alert fires server-side.
  useAlertChannel(orgId, (env) => {
    if (env.type === "alert.fired" || env.type === "alert.cleared") {
      qc.invalidateQueries({ queryKey: qk.safety.alerts.all });
    }
  });

  return null;
}
